import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import Login from "./pages/Login";
import Layout from "./components/Layout";
import ParentDashboard from "./pages/ParentDashboard";
import ParentNotifications from "./pages/ParentNotifications";
import ParentPhotos from "./pages/ParentPhotos";
import ParentDocuments from "./pages/ParentDocuments";
import AdminDashboard from "./pages/AdminDashboard";
import AdminParents from "./pages/AdminParents";
import AdminBulkUpload from "./pages/AdminBulkUpload";
import AdminNotifications from "./pages/AdminNotifications";
import AdminPhotos from "./pages/AdminPhotos";
import AdminDocuments from "./pages/AdminDocuments";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import AdminDailyDigest from "./pages/AdminDailyDigest";
import AdminEvents from "./pages/AdminEvents";
import ParentEvents from "./pages/ParentEvents";

function ProtectedRoute({ role, children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    if (role && user.role !== role) {
        return <Navigate to={user.role === "admin" ? "/admin" : "/parent"} replace />;
    }
    return children;
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    );
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={user.role === "admin" ? "/admin" : "/parent"} replace />;
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <Toaster position="top-right" richColors />
                    <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/login" element={<Login />} />

                        {/* Parent */}
                        <Route
                            path="/parent"
                            element={
                                <ProtectedRoute role="parent">
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<ParentDashboard />} />
                            <Route path="notifications" element={<ParentNotifications />} />
                            <Route path="events" element={<ParentEvents />} />
                            <Route path="photos" element={<ParentPhotos />} />
                            <Route path="documents" element={<ParentDocuments />} />
                        </Route>

                        {/* Admin */}
                        <Route
                            path="/admin"
                            element={
                                <ProtectedRoute role="admin">
                                    <Layout />
                                </ProtectedRoute>
                            }
                        >
                            <Route index element={<AdminDashboard />} />
                            <Route path="parents" element={<AdminParents />} />
                            <Route path="bulk-upload" element={<AdminBulkUpload />} />
                            <Route path="notifications" element={<AdminNotifications />} />
                            <Route path="events" element={<AdminEvents />} />
                            <Route path="photos" element={<AdminPhotos />} />
                            <Route path="documents" element={<AdminDocuments />} />
                            <Route path="daily-digest" element={<AdminDailyDigest />} />
                            <Route path="audit-logs" element={<AdminAuditLogs />} />
                        </Route>

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
